import { create } from 'zustand';
import type { Note } from '../generated/gql-types';
import { getApolloClient } from '../lib/getApolloClient';
import { CREATE_NOTE, DELETE_NOTE, GET_NOTES_FOR_OBJECT } from './note.gql';

interface NoteStore {
  notes: Note[];
  loadedObjectIds: string[];
  isLoading: boolean;
  isCreating: boolean;
  deletingNoteId: string | null;
  error: string | null;
  fetchNotesForObject: (objectId: string) => Promise<Note[]>;
  ensureNotesForObject: (objectId: string) => Promise<Note[]>;
  createNote: (objectId: string, content: string) => Promise<Note>;
  deleteNote: (noteId: string) => Promise<void>;
  getNotesForObject: (objectId: string) => Note[];
}

const sortNotesNewestFirst = (notes: Note[]): Note[] =>
  [...notes].sort(
    (firstNote, secondNote) =>
      Number(secondNote.createdAt) - Number(firstNote.createdAt),
  );

export const useNoteStore = create<NoteStore>((set, get) => ({
  notes: [],
  loadedObjectIds: [],
  isLoading: false,
  isCreating: false,
  deletingNoteId: null,
  error: null,

  fetchNotesForObject: async (objectId) => {
    set({ isLoading: true, error: null });

    try {
      const { data } = await getApolloClient().query({
        query: GET_NOTES_FOR_OBJECT,
        variables: { objectId },
        fetchPolicy: 'network-only',
      });
      const fetchedNotes = sortNotesNewestFirst(data.getNotesForObject);

      set((state) => ({
        notes: [
          ...state.notes.filter((note) => note.objectId !== objectId),
          ...fetchedNotes,
        ],
        loadedObjectIds: state.loadedObjectIds.includes(objectId)
          ? state.loadedObjectIds
          : [...state.loadedObjectIds, objectId],
      }));

      return fetchedNotes;
    } catch (fetchError) {
      set({ error: 'Unable to load notes' });
      throw fetchError;
    } finally {
      set({ isLoading: false });
    }
  },

  ensureNotesForObject: async (objectId) => {
    if (get().loadedObjectIds.includes(objectId)) {
      return get().getNotesForObject(objectId);
    }

    return get().fetchNotesForObject(objectId);
  },

  createNote: async (objectId, content) => {
    set({ isCreating: true, error: null });

    try {
      const { data } = await getApolloClient().mutate({
        mutation: CREATE_NOTE,
        variables: { input: { objectId, content } },
      });
      const createdNote = data.createNote as Note;

      set((state) => ({
        notes: sortNotesNewestFirst([
          ...state.notes.filter((note) => note.id !== createdNote.id),
          createdNote,
        ]),
        loadedObjectIds: state.loadedObjectIds.includes(objectId)
          ? state.loadedObjectIds
          : [...state.loadedObjectIds, objectId],
      }));

      return createdNote;
    } catch (createError) {
      set({ error: 'Unable to save note' });
      throw createError;
    } finally {
      set({ isCreating: false });
    }
  },

  deleteNote: async (noteId) => {
    set({ deletingNoteId: noteId, error: null });

    try {
      await getApolloClient().mutate({
        mutation: DELETE_NOTE,
        variables: { id: noteId },
      });

      set((state) => ({
        notes: state.notes.filter((note) => note.id !== noteId),
      }));
    } catch (deleteError) {
      set({ error: 'Unable to delete note' });
      throw deleteError;
    } finally {
      set({ deletingNoteId: null });
    }
  },

  getNotesForObject: (objectId) =>
    sortNotesNewestFirst(
      get().notes.filter((note) => note.objectId === objectId),
    ),
}));
